import type { ChartMetric, DashboardVM } from '../../lib/compute';
import { MetricBarChart, RevenueSpendChart } from '../charts';
import { KpiCard, PeriodTable } from '../ui';

// KPI cards map 1:1 to the chart metric they select (same order as vm.kpis).
const KPI_METRICS: ChartMetric[] = ['umsatz', 'spend', 'orders', 'aov'];

export function Cockpit({
  vm, onMetric, onSelectBucket,
}: { vm: DashboardVM; onMetric: (m: ChartMetric) => void; onSelectBucket: (i: number) => void }) {
  const metric = vm.cockpitMetric;
  return (
    <>
      <div className="cw-pagehead">
        <h1>Überblick Gruppe</h1>
        <span className="meta">{vm.rangeCaption}</span>
      </div>

      <div className="cw-kpis">
        <div
          className={`cw-hero cw-clickable${metric === 'roas' ? ' cw-selected' : ''}`}
          role="button"
          tabIndex={0}
          aria-pressed={metric === 'roas'}
          aria-label="ROAS als Diagramm-Metrik wählen"
          onClick={() => onMetric('roas')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onMetric('roas');
            }
          }}
        >
          <div className="cw-hero-label">ROAS</div>
          <div className="cw-hero-value cw-num">{vm.hero.roas}</div>
          <div className="cw-hero-delta">
            <span className="d cw-num" style={{ color: vm.hero.deltaColor }}>{vm.hero.deltaLabel}</span>
          </div>
          <div className="cw-hero-foot">{vm.hero.note}</div>
        </div>
        {vm.kpis.map((k, i) => (
          <KpiCard
            key={k.label}
            kpi={k}
            vsPrev
            onClick={() => onMetric(KPI_METRICS[i])}
            selected={metric === KPI_METRICS[i]}
          />
        ))}
      </div>

      {vm.channelsFiltered && (
        <div className="cw-roas-note">{vm.roasNote}</div>
      )}

      <div className="cw-panels">
        <section className="cw-card" style={{ padding: '22px 24px 18px' }}>
          <div className="cw-card-head">
            <h2>
              {metric === 'roas' ? 'Umsatz & Werbeausgaben' : vm.metricTitle} nach {vm.periodTitle}
            </h2>
          </div>
          {metric === 'roas' ? (
            <>
              <div className="cw-legend">
                <div className="item"><span className="sq" style={{ background: '#4A7FB5' }} /><span className="lbl">Umsatz</span></div>
                <div className="item"><span className="sq" style={{ background: '#D9D2C8' }} /><span className="lbl">Werbeausgaben</span></div>
                <div className="item"><span className="ln" /><span className="lbl">ROAS</span></div>
              </div>
              <RevenueSpendChart chart={vm.chart} onSelect={onSelectBucket} selected={vm.selBucket} />
            </>
          ) : (
            <>
              <div className="cw-legend">
                <div className="item"><span className="sq" style={{ background: vm.metricChart?.color }} /><span className="lbl">{vm.metricTitle}</span></div>
              </div>
              {vm.metricChart && <MetricBarChart chart={vm.metricChart} label={`${vm.metricTitle} nach ${vm.periodTitle}`} onSelect={onSelectBucket} selected={vm.selBucket} />}
            </>
          )}
          <PeriodTable periodHead={vm.periodHead} cols={vm.periodCols} rows={vm.periodTable} />
        </section>

        <section className="cw-card" style={{ padding: '22px 24px' }}>
          <div className="cw-card-head" style={{ marginBottom: 6 }}>
            <h2>{vm.breakdown.title}{vm.breakdown.bucketLabel ? ` · ${vm.breakdown.bucketLabel}` : ''}</h2>
            <span className="meta">
              {vm.breakdown.sublabel}
              {vm.breakdown.bucketLabel != null && vm.selBucket != null ? (
                <button className="cw-link-reset" onClick={() => onSelectBucket(vm.selBucket as number)}>gesamter Zeitraum</button>
              ) : (
                ` · ${vm.periodTitle} im Diagramm wählen`
              )}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {vm.breakdown.rows.map((r) => (
              <div key={r.name} className="cw-chan-row">
                <div className="cw-chan-top">
                  <div className="cw-chan-name">
                    <span className="dot" style={{ background: r.color }} />
                    <span className="nm">{r.name}</span>
                  </div>
                  <span className={`cw-chan-ums cw-num${r.pending ? ' cw-pending' : ''}`}>{r.value}</span>
                </div>
                <div className="cw-bar-track">
                  <div className="cw-bar-fill" style={{ width: r.barW, background: r.color }} />
                </div>
                <div className="cw-chan-meta">
                  <span className="s cw-num">{r.sub}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="cw-card" style={{ padding: '22px 24px' }}>
        <div className="cw-card-head" style={{ marginBottom: 10 }}>
          <h2>Umsatz nach Land</h2>
          <span className="meta">{vm.revenueLand.note} · {vm.rangeCaption}</span>
        </div>
        {vm.revenueLand.rows.length === 0 ? (
          <div className="cw-chan-meta"><span className="s">Kein Umsatz im Zeitraum.</span></div>
        ) : (
          <div className="cw-land-grid">
            {vm.revenueLand.rows.map((r) => (
              <div key={r.name} className="cw-chan-row">
                <div className="cw-chan-top">
                  <div className="cw-chan-name">
                    <span className="dot" style={{ background: '#E7A569' }} />
                    <span className="nm">{r.name}</span>
                  </div>
                  <span className="cw-chan-ums cw-num">{r.value}</span>
                </div>
                <div className="cw-bar-track">
                  <div className="cw-bar-fill" style={{ width: r.barW, background: '#E7A569' }} />
                </div>
                <div className="cw-chan-meta"><span className="s cw-num">{r.sub}</span></div>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="cw-sources">{vm.sources}</div>
    </>
  );
}
